import React, {useState, useEffect} from 'react';
import {deleteCard, updateCard} from '../api/learning-cards';
import ConfirmationModal from './ConfirmationModal';
import TextWithReading from './TextWithReading';
import { CARD_MARKS, CARD_MARK_LABELS, CARD_MARK_COLORS, CARD_SIDE_TYPE_LABELS } from '../constants/learning-cards';
import { extractReadingPairs } from '../utils/cardUtils';

function FlashCard({card, cardProgress, group, onCardUpdated}) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [formData, setFormData] = useState({
        side1_text: '',
        side2_text: ''
    });
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    /**
     * Extract text and reading pairs from card data
     */
    const getCardTexts = (cardData) => {
        if (cardData.sides && Array.isArray(cardData.sides) && cardData.sides.length >= 2) {
            const frontText = cardData.sides[0]?.text || '';
            const backText = cardData.sides[1]?.text || '';
            const frontReadings = extractReadingPairs(cardData.sides[0]);
            const backReadings = extractReadingPairs(cardData.sides[1]);

            return {
                frontText,
                backText,
                frontReadings,
                backReadings
            };
        }
        return {
            frontText: cardData.frontText || cardData.front_text || '',
            backText: cardData.backText || cardData.back_text || '',
            frontReadings: [],
            backReadings: []
        };
    };

    const { frontText, backText, frontReadings, backReadings } = getCardTexts(card);

    useEffect(() => {
        if (card) {
            setFormData({
                side1_text: frontText,
                side2_text: backText
            });
        }
    }, [card, frontText, backText]);

    useEffect(() => {
        const originalData = {
            side1_text: frontText,
            side2_text: backText
        };

        setHasChanges(
            formData.side1_text !== originalData.side1_text ||
            formData.side2_text !== originalData.side2_text
        );
    }, [formData, frontText, backText]);

    const getFontSize = (text) => {
        const length = text.length;
        if (length < 3) return 'fs-1';
        if (length < 7) return 'fs-2';
        if (length < 10) return 'fs-3';
        if (length < 15) return 'fs-4';
        if (length < 20) return 'fs-5';
        if (length < 30) return 'fs-6';
        return 'text-base truncate';
    };

    const getMarkBadge = () => {
        if (!cardProgress) {
            return (
                <span className="badge bg-secondary position-absolute top-0 end-0 m-1">
                    New
                </span>
            );
        }

        const mark = cardProgress.mark || 'MARK_NULL';
        const label = CARD_MARK_LABELS[mark] || 'New';
        const colorClass = CARD_MARK_COLORS[mark] || 'bg-secondary';

        return (
            <span className={`badge ${colorClass} position-absolute top-0 end-0 m-1`}>
                {label}
            </span>
        );
    };

    const getSideLabel = (sideIndex) => {
        const groupCardSideTypes = group?.cardSideTypes || group?.card_side_types || [];
        const sideType = groupCardSideTypes[sideIndex] || 'CARD_SIDE_NULL';
        const typeLabel = CARD_SIDE_TYPE_LABELS[sideType] || 'Standard';
        return `Side ${sideIndex + 1} (${typeLabel})`;
    };

    const handleDeleteCard = async () => {
        setIsDeleting(true);
        try {
            const result = await deleteCard(card.id);

            if (result.success) {
                if (onCardUpdated) {
                    onCardUpdated();
                }
            } else {
                console.error('Failed to delete card:', result.message);
            }
        } catch (err) {
            console.error('Error deleting card:', err);
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const handleChange = (e) => {
        const {id, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSave = async () => {
        if (!hasChanges) return;

        setIsSaving(true);
        try {
            const result = await updateCard(card.id, {
                sides_text: [formData.side1_text.trim(), formData.side2_text.trim()]
            });

            if (result.success) {
                setShowEditModal(false);
                if (onCardUpdated) {
                    onCardUpdated();
                }
            } else {
                console.error('Failed to update card:', result.message);
            }
        } catch (err) {
            console.error('Error updating card:', err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div
                className="card shadow-sm h-100 position-relative"
                onClick={() => {
                    setFormData({
                        side1_text: frontText,
                        side2_text: backText
                    });
                    setShowEditModal(true);
                }}
                style={{
                    cursor: 'pointer',
                    aspectRatio: '1/1',
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: '0.9rem'
                }}
            >
                {getMarkBadge()}

                <div
                    className="d-flex flex-column p-0 overflow-hidden h-100"
                >
                    <div
                        className="p-2 bg-white d-flex align-items-center justify-content-center"
                        style={{
                            height: '50%',
                            overflow: 'auto',
                            borderBottom: '1px solid rgba(0,0,0,0.125)'
                        }}
                    >
                        {frontReadings.length > 0 ? (
                            <TextWithReading
                                text={frontText}
                                readingPairs={frontReadings}
                                className={`card-text text-center text-with-readings ${getFontSize(frontText)}`}
                            />
                        ) : (
                            <div className={`card-text text-center ${getFontSize(frontText)}`}>
                                {frontText || 'No front text'}
                            </div>
                        )}
                    </div>

                    <div
                        className="p-2 bg-light d-flex align-items-center justify-content-center text-primary"
                        style={{
                            height: '50%',
                            overflow: 'auto'
                        }}
                    >
                        {backReadings.length > 0 ? (
                            <TextWithReading
                                text={backText}
                                readingPairs={backReadings}
                                className={`card-text text-center text-with-readings ${getFontSize(backText)}`}
                            />
                        ) : (
                            <div className={`card-text text-center ${getFontSize(backText)}`}>
                                {backText || 'No back text'}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showEditModal && (
                <>
                    <div
                        className="modal-backdrop fade show"
                        style={{zIndex: 1040}}
                        onClick={() => setShowEditModal(false)}
                    ></div>

                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        role="dialog"
                        style={{zIndex: 1050}}
                    >
                        <div
                            className="modal-dialog modal-dialog-centered"
                            role="document"
                            style={{pointerEvents: 'auto'}}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        Edit Card
                                        {cardProgress && (
                                            <span className={`ms-2 badge ${CARD_MARK_COLORS[cardProgress.mark] || 'bg-secondary'}`}>
                                                {CARD_MARK_LABELS[cardProgress.mark] || 'New'}
                                            </span>
                                        )}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowEditModal(false)}
                                        aria-label="Close"
                                    ></button>
                                </div>

                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="side1_text" className="form-label">{getSideLabel(0)}</label>
                                        <textarea
                                            className="form-control"
                                            id="side1_text"
                                            rows="3"
                                            value={formData.side1_text}
                                            onChange={handleChange}
                                            placeholder="Enter text for the first side"
                                        ></textarea>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="side2_text" className="form-label">{getSideLabel(1)}</label>
                                        <textarea
                                            className="form-control"
                                            id="side2_text"
                                            rows="3"
                                            value={formData.side2_text}
                                            onChange={handleChange}
                                            placeholder="Enter text for the second side"
                                        ></textarea>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger me-auto"
                                        onClick={() => setShowDeleteModal(true)}
                                    >
                                        Delete
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={!hasChanges || isSaving}
                                    >
                                        {isSaving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"
                                                      aria-hidden="true"></span>
                                                Saving...
                                            </>
                                        ) : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <ConfirmationModal
                show={showDeleteModal}
                title="Delete Card"
                message="Are you sure you want to delete this card? This action cannot be undone."
                confirmText="Delete Card"
                cancelText="Cancel"
                onConfirm={handleDeleteCard}
                onCancel={() => setShowDeleteModal(false)}
                isLoading={isDeleting}
            />
        </>
    );
}

export default FlashCard;